import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import {
  MapPinned,
  Wand2,
  Languages,
  Sparkles,
  Briefcase,
  Crown,
  ShieldCheck,
  Plane,
  Banknote,
  MessageCircle,
  Compass,
  Heart,
} from "lucide-react";

const REASONS = [
  { icon: MapPinned, title: "Local China Expertise", body: "On-the-ground curators in Beijing, Shanghai, Guangzhou, Chengdu and beyond." },
  { icon: Wand2, title: "Personalized Itineraries", body: "Hand-crafted around your interests, pace and budget — never copy-paste." },
  { icon: Languages, title: "English-Speaking Support", body: "Bilingual guides and 24/7 WhatsApp concierge in English (and Arabic, Mandarin, more)." },
  { icon: Sparkles, title: "AI-Powered Planning", body: "Mei, our Claude-powered concierge, drafts your full plan in 30 seconds." },
  { icon: Briefcase, title: "Business Travel Expertise", body: "Canton Fair, supplier audits, factory visits, exec transfers — handled end-to-end." },
  { icon: Crown, title: "Luxury & VIP Experiences", body: "Private guides, fine dining, exclusive cultural access at the highest tier." },
];

const CONFIDENCE = [
  { icon: ShieldCheck, title: "Visa Guidance", body: "From visa-free entry to L/M/X1 applications — we file or guide you." },
  { icon: Plane, title: "Transportation Assistance", body: "Bullet trains, domestic flights, private drivers — booked and confirmed." },
  { icon: Banknote, title: "Digital Payment Support", body: "We help set up Alipay & WeChat Pay so your foreign cards work everywhere." },
  { icon: MessageCircle, title: "English Communication", body: "Translators in every city, plus instant WhatsApp help during your trip." },
  { icon: Compass, title: "Local Travel Tips", body: "Apps, etiquette, safe neighborhoods, hidden gems — briefed before you fly." },
  { icon: Heart, title: "Safe & Comfortable", body: "Vetted hotels, halal-friendly dining, women-safe routes, family-tested vendors." },
];

const WhyUs = () => {
  const { t } = useLang();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="why-us" data-testid="why-us-section" className="bg-[#F7F5F0] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">
            ✦ Why The China Explorer
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            Travel China with someone who actually lives it.
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            Based in Dubai, rooted in China — an international perspective with deep local
            expertise across 30+ cities.
          </p>
        </SectionReveal>

        {/* Why us grid */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <SectionReveal
                key={r.title}
                delay={i * 0.06}
                data-testid={`why-card-${i}`}
                className="group bg-white border border-[#EAE6DF] p-7 hover:border-[#C62828]/30 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#C62828]/10 text-[#C62828] group-hover:bg-[#C62828] group-hover:text-white transition-colors duration-500">
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading text-2xl text-[#121212]">{r.title}</h3>
                <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{r.body}</p>
              </SectionReveal>
            );
          })}
        </div>

        {/* Travel with Confidence */}
        <SectionReveal delay={0.15} className="mt-24">
          <div className="bg-[#0E2A5C] text-white p-8 md:p-14">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                  ✦ Travel With Confidence
                </p>
                <h3 className="font-heading text-3xl md:text-4xl leading-tight">
                  Every concern of an international traveler — already solved.
                </h3>
                <button
                  data-testid="confidence-cta"
                  onClick={() => scrollTo("itinerary")}
                  className="mt-8 bg-[#D4AF37] hover:bg-[#bf9a2b] text-[#0E2A5C] px-7 py-3.5 uppercase tracking-wider text-sm font-bold transition-colors"
                >
                  Plan My China Journey →
                </button>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                {CONFIDENCE.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <div
                      key={c.title}
                      data-testid={`confidence-card-${i}`}
                      className="bg-white/[0.05] backdrop-blur-sm border border-white/10 p-5"
                    >
                      <Icon size={20} className="text-[#D4AF37]" />
                      <p className="mt-3 font-heading text-lg text-white">{c.title}</p>
                      <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{c.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
};

export default WhyUs;
